/** 샵관리 — 출고관리. 수동 출고(제품판매·폐기·조정)는 FIFO 로 차감, 시술 출고는 시술기록에서 자동 생성(여기선 조회만) */
import React from 'react';
import { hiCol } from 'components/grid/hiGrid';
import ShopGridPage from './ShopGridPage';
import {
  loadTable, saveTable, createUid, num, today, productOptions, issueManual, revertOuts,
  OUT_REASON_OPTIONS, MANUAL_OUT_REASON_OPTIONS,
} from 'shop/shopStore';
import { selectField, dateRangeFields, inRange, lockExisting } from 'shop/shopPageHelpers';

const decorate = (tc) => {
  const lots = new Map(loadTable(tc, 'stockIn').map((l) => [l.lotUid, l]));
  const products = new Map(loadTable(tc, 'product').map((p) => [p.productUid, p]));
  return (o) => ({
    ...o,
    mod: '',
    lotLabel: lots.get(o.lotUid) ? `${lots.get(o.lotUid).inDate} 입고분` : '',
    useUnit: products.get(o.productUid)?.useUnit || '',
  });
};

const filter = (rows, v) =>
  rows.filter(
    (r) => r.mod !== 'D'
      && (!v.productUid || r.productUid === v.productUid)
      && (!v.reason || r.reason === v.reason)
      && inRange(r.outDate, v.fromDate, v.toDate)
  );

const cfg = {
  gridId: 'sh-stock-out',
  title: '출고관리',
  uidKey: 'outUid',
  searchDefaults: { productUid: '', reason: '', fromDate: '', toDate: '' },
  searchFields: ({ tc }) => [[
    ...dateRangeFields(),
    selectField('productUid', '제품', productOptions(tc), '200px'),
    selectField('reason', '사유', OUT_REASON_OPTIONS),
  ]],
  columns: ({ tc }) => [
    lockExisting(hiCol.edDateColumn({ header: '출고일자', field: 'outDate', size: 120, required: true })),
    lockExisting(hiCol.edComboColumn({ header: '제품', field: 'productUid', size: 200, required: true, options: productOptions(tc), align: 'left' })),
    lockExisting(hiCol.edNumberColumn({ header: '출고수량', field: 'outQty', size: 100 })),
    hiCol.roTextColumn({ header: '사용단위', field: 'useUnit', size: 80, align: 'center' }),
    lockExisting(hiCol.edComboColumn({ header: '사유', field: 'reason', size: 100, required: true, options: OUT_REASON_OPTIONS })),
    hiCol.roTextColumn({ header: '차감 입고분(FIFO)', field: 'lotLabel', size: 140 }),
    hiCol.roNumberColumn({ header: '단가', field: 'unitCost', size: 100 }),
    hiCol.roNumberColumn({ header: '금액(원가)', field: 'amount', size: 110 }),
    lockExisting(hiCol.edTextColumn({ header: '비고', field: 'remark', size: 180 })),
  ],
  load: (tc) => loadTable(tc, 'stockOut').map(decorate(tc)),
  filter,
  emptyRow: () => ({ outUid: createUid('OUT'), outDate: today(), productUid: '', outQty: 1, reason: 'SALE', remark: '' }),
  validate: (rows, tc) => {
    const prev = new Set(loadTable(tc, 'stockOut').map((o) => o.outUid));
    const errors = [];
    for (const r of rows) {
      const isNew = !prev.has(r.outUid);
      if (r.mod === 'D') {
        if (!isNew && r.reason === 'SERVICE') errors.push('시술 출고는 시술기록을 삭제해야 취소됩니다.');
        continue;
      }
      if (!isNew) continue;
      if (!r.productUid) errors.push('제품을 선택하세요.');
      if (num(r.outQty) <= 0) errors.push('출고수량은 0보다 커야 합니다.');
      if (!MANUAL_OUT_REASON_OPTIONS.some((o) => o.value === r.reason)) {
        errors.push('수동 출고 사유는 제품판매·폐기·재고조정만 가능합니다.');
      }
    }
    return errors;
  },
  save: (tc, rows) => {
    const prevUids = new Set(loadTable(tc, 'stockOut').map((o) => o.outUid));
    const snapshot = ['stockIn', 'stockOut'].map((n) => [n, loadTable(tc, n)]);
    try {
      const removed = rows.filter((r) => r.mod === 'D' && prevUids.has(r.outUid)).map((r) => r.outUid);
      if (removed.length) revertOuts(tc, removed);
      rows
        .filter((r) => r.mod !== 'D' && !prevUids.has(r.outUid))
        .forEach((r) => issueManual(tc, {
          productUid: r.productUid, outDate: r.outDate, qty: r.outQty, reason: r.reason, remark: r.remark,
        }));
    } catch (e) {
      // 재고 부족 등 — 이번 저장 전체 취소
      snapshot.forEach(([n, data]) => saveTable(tc, n, data));
      throw e;
    }
  },
};

const StockOut = () => <ShopGridPage cfg={cfg} />;
export default StockOut;
